import React, { useState, useEffect, useRef, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import html2canvas from 'html2canvas';
import './DiseaseFamilyCard.css';
import DiseaseDropdown from './DiseaseDropdown';

// --- Helper Functions ---
const toTitleCase = (str) => {
    if (!str || typeof str !== 'string') return '';
    return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const getInitials = (name = '') => {
    if (!name || typeof name !== 'string') return '';
    return name.split(/[\s-]+/).map(word => word[0]).filter(char => char && char.match(/[a-zA-Z0-9]/)).slice(0, 3).join('').toUpperCase();
};

const wrapText = (context, text, x, y, maxWidth, lineHeight) => {
    const words = text.split(' ');
    let line = '';
    let lineCount = 0;
    const maxLines = 3;
    for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = context.measureText(testLine);
        if (metrics.width > maxWidth && n > 0) {
            if (lineCount >= maxLines - 1) { context.fillText(line.trim() + '...', x, y); return; }
            context.fillText(line.trim(), x, y);
            line = words[n] + ' ';
            y += lineHeight;
            lineCount++;
        } else { line = testLine; }
    }
    if (lineCount < maxLines) { context.fillText(line.trim(), x, y); }
};

// --- Child Components ---

const MappingInfo = ({ mappingResult }) => {
    if (!mappingResult || !mappingResult.matched_oncotree_node) return null;
    const { do_id, do_name, matched_oncotree_node, confidence_score, mapping_method } = mappingResult;
    return (
        <div className="ONCRP-TOOLS-QP-df-mapping-info">
            <div className="ONCRP-TOOLS-QP-df-mapping-header"><i className="fas fa-bullseye"></i> OncoTree Mapping Details</div>
            <div className="ONCRP-TOOLS-QP-df-mapping-body">
                <p><strong>Query:</strong> {do_name} ({do_id})</p>
                <p className="ONCRP-TOOLS-QP-df-mapping-success"><strong>Mapped To:</strong> {matched_oncotree_node.name} ({matched_oncotree_node.code})</p>
                <p><strong>Tissue:</strong> {toTitleCase(matched_oncotree_node.tissue)}</p>
                <p><strong>Main Type:</strong> {toTitleCase(matched_oncotree_node.mainType)}</p>
                <p><strong>Confidence:</strong> {confidence_score ? (confidence_score * 100).toFixed(0) : 'N/A'}%</p>
                <p><strong>Method:</strong> {mapping_method}</p>
            </div>
        </div>
    );
};

const GraphLegend = ({ hasOncoTree }) => (
    <div className="ONCRP-TOOLS-QP-df-legend">
        <div className="ONCRP-TOOLS-QP-df-legend-section">
            <div className="ONCRP-TOOLS-QP-df-legend-item"><span className="legend-swatch query"></span>Query Disease (DO)</div>
            <div className="ONCRP-TOOLS-QP-df-legend-item"><span className="legend-swatch do-ancestor"></span>DO Ancestor</div>
            <div className="ONCRP-TOOLS-QP-df-legend-item"><span className="legend-swatch do-descendant"></span>DO Descendant</div>
        </div>
        {hasOncoTree && (
            <div className="ONCRP-TOOLS-QP-df-legend-section">
                <div className="ONCRP-TOOLS-QP-df-legend-item"><span className="legend-swatch mapped"></span>Mapped OncoTree</div>
                <div className="ONCRP-TOOLS-QP-df-legend-item"><span className="legend-swatch onco-ancestor"></span>OncoTree Ancestor</div>
                <div className="ONCRP-TOOLS-QP-df-legend-item"><span className="legend-swatch onco-descendant"></span>OncoTree Descendant</div>
            </div>
        )}
        <div className="ONCRP-TOOLS-QP-df-legend-section">
             <div className="ONCRP-TOOLS-QP-df-legend-item"><span className="legend-line do-hierarchy"></span>DO Hierarchy</div>
             {hasOncoTree && <div className="ONCRP-TOOLS-QP-df-legend-item"><span className="legend-line onco-hierarchy"></span>OncoTree Hierarchy</div>}
             {hasOncoTree && <div className="ONCRP-TOOLS-QP-df-legend-item"><span className="legend-line maps-to"></span>DO ↔ OncoTree Map</div>}
        </div>
    </div>
);


// --- Main Component ---

const DiseaseFamilyCard = ({ diseaseTree, associatedDiseases }) => {
    const [selectedDiseaseName, setSelectedDiseaseName] = useState('');
    const [networkType, setNetworkType] = useState('combined');
    const [graphData, setGraphData] = useState({ nodes: [], links: [] });
    const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
    const [isLightTheme, setIsLightTheme] = useState(false);
    const graphRef = useRef();
    const containerRef = useRef();
    const networkContainerRef = useRef();

    const handleSnapshot = async () => {
        if (!networkContainerRef.current) return;
        try {
            const canvas = await html2canvas(networkContainerRef.current, {
                useCORS: true,
                allowTaint: true,
                backgroundColor: null,
                scale: 2,
                logging: false,
                ignoreElements: (element) => element.classList.contains('ONCRP-TOOLS-QP-snapshot-btn')
            });
            const dataUrl = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.download = `disease_family_${selectedDiseaseName?.replace(/\s+/g, '_') || 'network'}_${new Date().toISOString().slice(0,10)}.png`;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            console.error('Failed to capture network:', e);
        }
    };

    // Theme detection effect
    useEffect(() => {
        const checkTheme = () => {
            if (typeof document !== 'undefined') {
                const hasLightClass = document.documentElement.classList.contains('ocrp-theme-light');
                setIsLightTheme(hasLightClass);
            }
        };

        checkTheme();

        if (typeof window !== 'undefined') {
            const handleThemeChange = (event) => {
                if (event.detail && typeof event.detail.theme === 'string') {
                    setIsLightTheme(event.detail.theme === 'light');
                }
            };

            window.addEventListener('ocrp-theme-change', handleThemeChange);
            return () => window.removeEventListener('ocrp-theme-change', handleThemeChange);
        }
    }, []);

    useEffect(() => {
        if (associatedDiseases?.length > 0) {
            const isSelectedDiseaseValid = associatedDiseases.some(d => d.name === selectedDiseaseName);
            if (!isSelectedDiseaseValid) {
                setSelectedDiseaseName(associatedDiseases[0].name);
            }
        }
    }, [associatedDiseases, selectedDiseaseName]);

    // Notify BodyVisual to highlight organ for selected disease
    useEffect(() => {
        if (!selectedDiseaseName) return;
        const ev = new CustomEvent('oncrp-highlight-disease', { detail: { diseaseName: selectedDiseaseName } });
        window.dispatchEvent(ev);
    }, [selectedDiseaseName]);

    useEffect(() => {
        const handleResize = () => { if (containerRef.current) { const { width, height } = containerRef.current.getBoundingClientRect(); setDimensions({ width: width > 0 ? width : 800, height: height > 0 ? height : 600 }); } };
        const resizeObserver = new ResizeObserver(handleResize);
        if (containerRef.current) resizeObserver.observe(containerRef.current);
        handleResize();
        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        if (!selectedDiseaseName || !diseaseTree?.[selectedDiseaseName]) {
            setGraphData({ nodes: [], links: [] }); return;
        }
        const currentDiseaseData = diseaseTree[selectedDiseaseName];
        if (!currentDiseaseData) {
            setGraphData({ nodes: [], links: [] }); return;
        }

        const { mapping_result, combined_network, do_network } = currentDiseaseData;
        const hasCombinedNetwork = combined_network?.nodes?.length > 0;
        const sourceNetwork = (networkType === 'combined' && hasCombinedNetwork) ? combined_network : do_network;
        
        if (!sourceNetwork?.nodes) {
            setGraphData({ nodes: [], links: [] }); return;
        }

        const queryDoId = mapping_result?.do_id;
        const queryOncoTreeId = mapping_result?.matched_oncotree_node?.code;
        
        const nodesById = new Map(sourceNetwork.nodes.map(node => [node.data.id, node.data]));
        const allEdges = (sourceNetwork.edges || []).map(edge => edge.data);

        const oncoTreeEdges = allEdges.filter(edge => {
            const sourceNode = nodesById.get(edge.source);
            const targetNode = nodesById.get(edge.target);
            return sourceNode?.graph_type === 'oncotree' && targetNode?.graph_type === 'oncotree';
        });

        const buildAdjMaps = (edges) => {
            const forward = new Map(), backward = new Map();
            for (const { source, target } of edges) {
                if (!forward.has(source)) forward.set(source, []);
                if (!backward.has(target)) backward.set(target, []);
                forward.get(source).push(target);
                backward.get(target).push(source);
            }
            return { forward, backward };
        };

        const globalAdj = buildAdjMaps(allEdges);
        const oncoTreeAdj = buildAdjMaps(oncoTreeEdges);

        const traverse = (startNode, adjMap) => {
            const visited = new Set(); if (!startNode || !adjMap.has(startNode)) return visited;
            const queue = [startNode]; visited.add(startNode); let head = 0;
            while (head < queue.length) {
                const current = queue[head++]; const neighbors = adjMap.get(current) || [];
                for (const neighbor of neighbors) {
                    if (!visited.has(neighbor)) { visited.add(neighbor); queue.push(neighbor); }
                }
            }
            visited.delete(startNode); return visited;
        };
        
        const doAncestors = traverse(queryDoId, globalAdj.forward);
        const doDescendants = traverse(queryDoId, globalAdj.backward);

        // *** CHANGE AS REQUESTED: OncoTree logic has been flipped ***
        const oncoTreeAncestors = traverse(queryOncoTreeId, oncoTreeAdj.backward); // Was forward
        const oncoTreeDescendants = traverse(queryOncoTreeId, oncoTreeAdj.forward);  // Was backward
        
        const processedNodes = sourceNetwork.nodes.map(nodeInfo => {
            const node = { ...nodeInfo.data }; const { id, graph_type = 'do' } = node;
            let role = 'default';
            if (id === queryDoId) role = 'query';
            else if (id === queryOncoTreeId) role = 'mappedOncoTree';
            else if (graph_type === 'do') {
                if (doAncestors.has(id)) role = 'doAncestor';
                else if (doDescendants.has(id)) role = 'doDescendant';
            } else if (graph_type === 'oncotree') {
                if (oncoTreeAncestors.has(id)) role = 'oncoTreeAncestor';
                else if (oncoTreeDescendants.has(id)) role = 'oncoTreeDescendant';
            }
            node.role = role; node.graphType = graph_type; node.name = node.name || node.label || id;
            return node;
        });

        const nodeIds = new Set(processedNodes.map(n => n.id));
        const processedLinks = allEdges.filter(edge => nodeIds.has(edge.source) && nodeIds.has(edge.target)).map(edge => ({ ...edge }));
        setGraphData({ nodes: processedNodes, links: processedLinks });
        setTimeout(() => { if (graphRef.current) graphRef.current.zoomToFit(400, 50); }, 300);
    }, [selectedDiseaseName, networkType, diseaseTree]);


    // Theme-aware stylish node rendering
    const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
        if (!Number.isFinite(node.x) || !Number.isFinite(node.y)) return;

        const roleColors = isLightTheme ? {
            query: { main: '#dc2626', light: '#ef4444' },
            mappedOncoTree: { main: '#16a34a', light: '#22c55e' },
            doAncestor: { main: '#2563eb', light: '#3b82f6' },
            doDescendant: { main: '#7c3aed', light: '#8b5cf6' },
            oncoTreeAncestor: { main: '#ca8a04', light: '#eab308' },
            oncoTreeDescendant: { main: '#0891b2', light: '#06b6d4' },
            default: { main: '#6b7280', light: '#9ca3af' }
        } : {
            query: { main: '#ff4757', light: '#ff6b81' },
            mappedOncoTree: { main: '#2ecc71', light: '#55efc4' },
            doAncestor: { main: '#3498db', light: '#74b9ff' },
            doDescendant: { main: '#9b59b6', light: '#c56cf0' },
            oncoTreeAncestor: { main: '#f1c40f', light: '#feca57' },
            oncoTreeDescendant: { main: '#1abc9c', light: '#48dbfb' },
            default: { main: '#7f8c8d', light: '#bdc3c7' }
        };
        const colors = roleColors[node.role] || roleColors.default;
        const radius = node.role === 'query' || node.role === 'mappedOncoTree' ? 14 : 10;
        
        ctx.shadowColor = colors.main;
        ctx.shadowBlur = 8;
        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, radius);
        gradient.addColorStop(0, colors.light);
        gradient.addColorStop(1, colors.main);
        ctx.fillStyle = gradient;
        
        ctx.beginPath(); ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false); ctx.fill();
        ctx.shadowBlur = 0;
        
        const initials = getInitials(node.name);
        const initialFontSize = radius * 0.8;
        ctx.font = `bold ${initialFontSize}px 'Montserrat', sans-serif`;
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(initials, node.x, node.y);
        
        const formattedDisplayText = toTitleCase(node.name);
        const labelFontSize = 8 / globalScale;
        ctx.font = `500 ${labelFontSize}px 'Inter', sans-serif`;
        ctx.fillStyle = isLightTheme ? '#1f2937' : '#f1f3f5';
        const textY = node.y + radius + labelFontSize;
        wrapText(ctx, formattedDisplayText, node.x, textY, 150 / globalScale, labelFontSize + 1);

    }, [isLightTheme]);
    
    // Theme-aware stylish link rendering
    const linkCanvasObject = useCallback((link, ctx, globalScale) => {
        if (!link.source || !link.target || !Number.isFinite(link.source.x) || !Number.isFinite(link.target.x)) return;
        const start = link.source, end = link.target;
        ctx.lineWidth = 1.5 / globalScale; ctx.setLineDash([]);
        const relation = link.relation || 'is_a';
        
        let color = isLightTheme ? 'rgba(37, 99, 235, 0.6)' : 'rgba(52, 152, 219, 0.4)';
        if (relation === 'parent_of') color = isLightTheme ? 'rgba(202, 138, 4, 0.7)' : 'rgba(241, 196, 15, 0.5)';
        else if (relation === 'maps_to') {
            color = isLightTheme ? '#dc2626' : '#ff4757';
            ctx.lineWidth = 2.0 / globalScale;
            ctx.setLineDash([5 / globalScale, 3 / globalScale]);
        }
        
        ctx.strokeStyle = color;
        ctx.shadowColor = color;
        ctx.shadowBlur = 8;
        ctx.beginPath(); ctx.moveTo(start.x, start.y); ctx.lineTo(end.x, end.y); ctx.stroke();
        ctx.shadowBlur = 0; ctx.setLineDash([]);
    }, [isLightTheme]);
    
    // Theme-aware tooltip and click handler
    const nodeLabel = useCallback(node => {
        const roleColors = isLightTheme ? {
            query: '#dc2626', mappedOncoTree: '#16a34a', doAncestor: '#2563eb', doDescendant: '#7c3aed', 
            oncoTreeAncestor: '#ca8a04', oncoTreeDescendant: '#0891b2', default: '#6b7280'
        } : {
            query: '#ff4757', mappedOncoTree: '#2ecc71', doAncestor: '#3498db', doDescendant: '#9b59b6', 
            oncoTreeAncestor: '#f1c40f', oncoTreeDescendant: '#1abc9c', default: '#7f8c8d'
        };
        const color = roleColors[node.role] || roleColors.default;
        
        let content = `<div class="node-tooltip"><div class="tooltip-header" style="border-left-color: ${color};"><strong>${toTitleCase(node.name)}</strong><span class="tooltip-id">${node.id}</span></div><div class="tooltip-body">`;
        if (node.def) content += `<p class="tooltip-def"><strong>Definition:</strong> ${node.def.replace(/\[(.*?)\]/g, '')}</p>`;
        if (node.mainType) content += `<p><strong>Main Type:</strong> ${node.mainType}</p>`;
        if (node.tissue) content += `<p><strong>Tissue:</strong> ${node.tissue}</p>`;
        if (node.synonym?.length > 0) content += `<p><strong>Synonyms:</strong> ${node.synonym.map(s => `"${s.replace(/" EXACT \[\]/, '')}"`).join(', ')}</p>`;
        if (content.endsWith('<div class="tooltip-body">')) content += '<p>No additional details available.</p>';
        content += `</div></div>`;
        return content;
    }, [isLightTheme]);
    
    const handleNodeClick = useCallback(node => {
        if (graphRef.current && node.x != null && node.y != null) {
            graphRef.current.centerAt(node.x, node.y, 1000);
            graphRef.current.zoom(2.5, 500);
        }
    }, []);

    const currentDiseaseData = diseaseTree?.[selectedDiseaseName];
    const hasCombinedNetwork = currentDiseaseData?.combined_network?.nodes?.length > 0;
    
    return (
        <div className="ONCRP-TOOLS-QP-card ONCRP-TOOLS-QP-span-col-6 ONCRP-TOOLS-QP-tier-decision-card">
            <div className="ONCRP-TOOLS-QP-card-header"><div className="ONCRP-TOOLS-QP-header-left"><i className="fa fa-sitemap"></i><h3>Disease Family</h3></div></div>
            <div className="ONCRP-TOOLS-QP-card-body" ref={containerRef}>
                <div className="ONCRP-TOOLS-QP-df-controls">
                <DiseaseDropdown
                            diseases={associatedDiseases || []}
                            selectedDiseaseName={selectedDiseaseName}
                            onSelect={(disease) => setSelectedDiseaseName(disease.name)}
                        />
                    <div className="ONCRP-TOOLS-QP-df-network-toggle">
                        <button onClick={() => setNetworkType('combined')} disabled={!hasCombinedNetwork} className={networkType === 'combined' ? 'active' : ''}>Combined</button>
                        <button onClick={() => setNetworkType('do')} className={networkType === 'do' ? 'active' : ''}>DO-Only</button>
                    </div>
                </div>
                
                <div className="ONCRP-TOOLS-QP-df-main-content">
                    <div className="ONCRP-TOOLS-QP-df-network-container" ref={networkContainerRef}>
                        <button className="ONCRP-TOOLS-QP-snapshot-btn" style={{ left: 'auto', right: '1vh' }} onClick={handleSnapshot} title="Save as PNG">
                            <i className="fas fa-camera"></i>
                            <span>Snapshot</span>
                        </button>
                        {graphData.nodes.length > 0 ? (
                            <ForceGraph2D 
                                ref={graphRef} 
                                graphData={graphData} 
                                nodeLabel={nodeLabel} 
                                onNodeClick={handleNodeClick} 
                                nodeCanvasObject={nodeCanvasObject} 
                                linkCanvasObject={linkCanvasObject} 
                                width={dimensions.width - 250}
                                height={dimensions.height - 60}
                                backgroundColor="rgba(0,0,0,0)"
                                cooldownTime={8000}
                                warmupTicks={100}
                                d3Force={(d3, forceName) => {
                                    if (forceName === 'charge') return d3.forceManyBody().strength(-150);
                                    if (forceName === 'link') return d3.forceLink().distance(link => link.relation === 'maps_to' ? 120 : 80);
                                }}
                            />
                        ) : <div className="ONCRP-TOOLS-QP-disease-family-placeholder"><span>No graph data to display for this selection.</span></div>}
                    </div>
                    
                    <div className="ONCRP-TOOLS-QP-df-info-column">
                        <GraphLegend hasOncoTree={hasCombinedNetwork} />
                        {networkType === 'combined' && hasCombinedNetwork && (
                            <MappingInfo mappingResult={currentDiseaseData?.mapping_result} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DiseaseFamilyCard;
